/** Ray-casting point-in-polygon for GeoJSON Polygon / MultiPolygon (lon/lat). */

type Position = [number, number];

function ringContains(lon: number, lat: number, ring: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi + 0.0) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function polygonContains(lon: number, lat: number, coordinates: Position[][]): boolean {
  if (!coordinates?.length) return false;
  if (!ringContains(lon, lat, coordinates[0] as Position[])) return false;
  // Holes
  for (let h = 1; h < coordinates.length; h++) {
    if (ringContains(lon, lat, coordinates[h] as Position[])) return false;
  }
  return true;
}

/**
 * Check whether lon/lat is inside a farm GeoJSON Feature, Polygon, or MultiPolygon.
 */
export function pointInFarmGeometry(
  lon: number,
  lat: number,
  geometry: any
): boolean {
  if (!geometry || !Number.isFinite(lon) || !Number.isFinite(lat)) return false;

  const geom =
    geometry.type === 'Feature'
      ? geometry.geometry
      : geometry.type === 'FeatureCollection'
        ? geometry.features?.[0]?.geometry
        : geometry;

  if (!geom?.type) return false;

  if (geom.type === 'Polygon') {
    return polygonContains(lon, lat, geom.coordinates);
  }
  if (geom.type === 'MultiPolygon') {
    return (geom.coordinates as Position[][][]).some((poly) => polygonContains(lon, lat, poly));
  }
  return false;
}

/** Haversine distance in meters. */
export function haversineMeters(
  lon1: number,
  lat1: number,
  lon2: number,
  lat2: number
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
